export const lessonsData = [
  {
    id: 1,
    title: 'Multi-Digit Multiplication',
    vocabulary: [
      { term: 'Product', definition: 'The answer to a multiplication problem' },
      { term: 'Partial Products', definition: 'Products obtained by multiplying each digit of one factor by each digit of the other' },
      { term: 'Area Model', definition: 'A visual representation showing multiplication as the area of a rectangle' },
      { term: 'Standard Algorithm', definition: 'The traditional method of multiplying numbers vertically' }
    ],
    conceptExplanation: `Multi-digit multiplication builds on your knowledge of basic multiplication facts. There are several strategies you can use:

**Area Model:** Break numbers into place values and multiply each part, then add the partial products.
Example: 23 × 14 = (20 × 10) + (20 × 4) + (3 × 10) + (3 × 4) = 200 + 80 + 30 + 12 = 322

**Partial Products Method:** Multiply each digit separately and add all products together.

**Standard Algorithm:** Multiply from right to left, regrouping when necessary.

The key is understanding that when you multiply by tens, hundreds, or thousands, you're really multiplying by 10, 100, or 1,000.`,
    examples: [
      {
        problem: '34 × 6',
        steps: [
          'Break 34 into 30 + 4',
          'Multiply 30 × 6 = 180',
          'Multiply 4 × 6 = 24',
          'Add the partial products: 180 + 24 = 204',
          'Answer: 34 × 6 = 204'
        ]
      },
      {
        problem: '52 × 23',
        steps: [
          'Set up vertically: 52 on top, 23 below',
          'Multiply 52 × 3 (ones place): 52 × 3 = 156',
          'Multiply 52 × 20 (tens place): 52 × 2 = 104, then add a zero → 1,040',
          'Add the partial products: 156 + 1,040 = 1,196',
          'Answer: 52 × 23 = 1,196'
        ]
      },
      {
        problem: '3,214 × 5',
        steps: [
          'Multiply ones: 4 × 5 = 20 (write 0, carry 2)',
          'Multiply tens: 1 × 5 = 5, plus 2 carried = 7',
          'Multiply hundreds: 2 × 5 = 10 (write 0, carry 1)',
          'Multiply thousands: 3 × 5 = 15, plus 1 carried = 16',
          'Answer: 3,214 × 5 = 16,070'
        ]
      }
    ],
    practiceQuestions: [
      {
        question: 'What is 47 × 8?',
        options: ['356', '376', '386', '396'],
        correctAnswer: 1,
        explanation: '47 × 8 = (40 × 8) + (7 × 8) = 320 + 56 = 376'
      },
      {
        question: 'Calculate 63 × 24',
        options: ['1,412', '1,502', '1,512', '1,612'],
        correctAnswer: 2,
        explanation: '63 × 24 = (63 × 20) + (63 × 4) = 1,260 + 252 = 1,512'
      },
      {
        question: 'A bakery makes 125 cookies per day. How many cookies do they make in 7 days?',
        options: ['775', '825', '875', '925'],
        correctAnswer: 2,
        explanation: '125 × 7 = (100 × 7) + (20 × 7) + (5 × 7) = 700 + 140 + 35 = 875 cookies'
      },
      {
        question: 'What is 2,345 × 6?',
        options: ['14,070', '14,170', '14,270', '14,370'],
        correctAnswer: 0,
        explanation: '2,345 × 6 = 14,070 (multiply each place value and regroup as needed)'
      }
    ],
    teacherTip: 'When multiplying multi-digit numbers, always estimate first! Round to the nearest ten or hundred to check if your answer makes sense. For 47 × 8, think "50 × 8 = 400, so my answer should be close to 400." This helps catch mistakes!'
  },
  {
    id: 2,
    title: 'Long Division',
    vocabulary: [
      { term: 'Dividend', definition: 'The number being divided' },
      { term: 'Divisor', definition: 'The number you are dividing by' },
      { term: 'Quotient', definition: 'The answer to a division problem' },
      { term: 'Remainder', definition: 'The amount left over after division' },
      { term: 'Partial Quotient', definition: 'A piece of the final quotient found by subtracting multiples of the divisor' }
    ],
    conceptExplanation: `Long division is a method for dividing large numbers. There are two main strategies:

**Partial Quotients Method:** Subtract multiples of the divisor until you can't subtract anymore. Add up all the multiples you subtracted—that's your quotient!

**Standard Algorithm:** Divide, multiply, subtract, bring down—repeat! This is the traditional method.

**Interpreting Remainders:**
- Sometimes the remainder is the answer (How many are left over?)
- Sometimes you round up (How many buses needed?)
- Sometimes you express it as a fraction or decimal

The key is understanding what the remainder means in the context of the problem.`,
    examples: [
      {
        problem: '156 ÷ 6',
        steps: [
          'How many 6s go into 15? Answer: 2 (write 2 above the 5)',
          'Multiply: 2 × 6 = 12. Subtract: 15 - 12 = 3',
          'Bring down the 6 to make 36',
          'How many 6s go into 36? Answer: 6 (write 6 above the 6)',
          'Multiply: 6 × 6 = 36. Subtract: 36 - 36 = 0',
          'Answer: 156 ÷ 6 = 26'
        ]
      },
      {
        problem: '847 ÷ 7',
        steps: [
          'How many 7s go into 8? Answer: 1 (write 1 above the 8)',
          'Multiply: 1 × 7 = 7. Subtract: 8 - 7 = 1',
          'Bring down the 4 to make 14',
          'How many 7s go into 14? Answer: 2 (write 2 above the 4)',
          'Multiply: 2 × 7 = 14. Subtract: 14 - 14 = 0',
          'Bring down the 7 to make 7',
          'How many 7s go into 7? Answer: 1 (write 1 above the 7)',
          'Multiply: 1 × 7 = 7. Subtract: 7 - 7 = 0',
          'Answer: 847 ÷ 7 = 121'
        ]
      },
      {
        problem: '235 ÷ 4 (with remainder)',
        steps: [
          'How many 4s go into 2? Answer: 0 (not enough)',
          'How many 4s go into 23? Answer: 5 (write 5 above the 3)',
          'Multiply: 5 × 4 = 20. Subtract: 23 - 20 = 3',
          'Bring down the 5 to make 35',
          'How many 4s go into 35? Answer: 8 (write 8 above the 5)',
          'Multiply: 8 × 4 = 32. Subtract: 35 - 32 = 3',
          'Answer: 235 ÷ 4 = 58 R3 (or 58 ¾)'
        ]
      }
    ],
    practiceQuestions: [
      {
        question: 'What is 144 ÷ 8?',
        options: ['16', '17', '18', '19'],
        correctAnswer: 2,
        explanation: '144 ÷ 8 = 18 (8 × 18 = 144)'
      },
      {
        question: 'Calculate 567 ÷ 9',
        options: ['61', '62', '63', '64'],
        correctAnswer: 2,
        explanation: '567 ÷ 9 = 63 (9 × 63 = 567)'
      },
      {
        question: 'A teacher has 175 pencils to share equally among 7 students. How many pencils does each student get?',
        options: ['23', '24', '25', '26'],
        correctAnswer: 2,
        explanation: '175 ÷ 7 = 25 pencils per student'
      },
      {
        question: 'What is 293 ÷ 5? (Express with remainder)',
        options: ['58 R2', '58 R3', '59 R2', '59 R3'],
        correctAnswer: 1,
        explanation: '293 ÷ 5 = 58 R3 (5 × 58 = 290, and 293 - 290 = 3)'
      }
    ],
    teacherTip: 'The phrase "Does McDonald\'s Sell Burgers?" helps you remember the division steps: Divide, Multiply, Subtract, Bring down! Also, always check your work by multiplying the quotient by the divisor and adding the remainder—you should get the dividend!'
  },
  {
    id: 3,
    title: 'Fractions: Equivalence & Comparison',
    vocabulary: [
      { term: 'Equivalent Fractions', definition: 'Fractions that represent the same value but have different numerators and denominators' },
      { term: 'Numerator', definition: 'The top number in a fraction (how many parts you have)' },
      { term: 'Denominator', definition: 'The bottom number in a fraction (how many equal parts in the whole)' },
      { term: 'Benchmark Fractions', definition: 'Common fractions like ½, ¼, and ¾ used for comparison' }
    ],
    conceptExplanation: `Understanding equivalent fractions is essential for working with fractions!

**Generating Equivalent Fractions:**
Multiply or divide both the numerator and denominator by the same number.
Example: ½ = 2/4 = 3/6 = 4/8 (multiply both by 2, then 3, then 4)

**Comparing Fractions with Unlike Denominators:**
Method 1: Find a common denominator
Method 2: Compare to benchmark fractions (½, ¼, ¾)
Method 3: Use visual models

**Key Understanding:**
When the numerator and denominator are the same (like 5/5), the fraction equals 1 whole.
The larger the denominator, the smaller each piece (1/8 is smaller than 1/4).`,
    examples: [
      {
        problem: 'Find three equivalent fractions for 2/3',
        steps: [
          'Multiply numerator and denominator by 2: (2×2)/(3×2) = 4/6',
          'Multiply numerator and denominator by 3: (2×3)/(3×3) = 6/9',
          'Multiply numerator and denominator by 4: (2×4)/(3×4) = 8/12',
          'Answer: 2/3 = 4/6 = 6/9 = 8/12 (all equivalent!)'
        ]
      },
      {
        problem: 'Compare 3/4 and 5/6 using common denominators',
        steps: [
          'Find a common denominator: 4 and 6 both go into 12',
          'Convert 3/4: (3×3)/(4×3) = 9/12',
          'Convert 5/6: (5×2)/(6×2) = 10/12',
          'Compare: 9/12 < 10/12',
          'Answer: 3/4 < 5/6'
        ]
      },
      {
        problem: 'Which is greater: 5/8 or 7/12?',
        steps: [
          'Find a common denominator: 8 and 12 both go into 24',
          'Convert 5/8: (5×3)/(8×3) = 15/24',
          'Convert 7/12: (7×2)/(12×2) = 14/24',
          'Compare: 15/24 > 14/24',
          'Answer: 5/8 > 7/12'
        ]
      }
    ],
    practiceQuestions: [
      {
        question: 'Which fraction is equivalent to 3/5?',
        options: ['5/8', '6/10', '9/12', '12/15'],
        correctAnswer: 1,
        explanation: '3/5 = 6/10 (multiply both numerator and denominator by 2)'
      },
      {
        question: 'Compare: 2/3 ___ 5/8',
        options: ['2/3 < 5/8', '2/3 > 5/8', '2/3 = 5/8', 'Cannot compare'],
        correctAnswer: 1,
        explanation: 'Using common denominator 24: 2/3 = 16/24 and 5/8 = 15/24, so 2/3 > 5/8'
      },
      {
        question: 'Which fraction is closest to ½?',
        options: ['3/8', '4/9', '5/12', '7/15'],
        correctAnswer: 1,
        explanation: '4/9 is closest to ½. Converting to decimals: 4/9 ≈ 0.44, which is very close to 0.5'
      },
      {
        question: 'Simplify 12/16 to its simplest form',
        options: ['6/8', '4/6', '3/4', '2/3'],
        correctAnswer: 2,
        explanation: '12/16 = 3/4 (divide both numerator and denominator by 4)'
      }
    ],
    teacherTip: 'When comparing fractions, if they have the same numerator, the one with the smaller denominator is larger! Think about pizza: would you rather have 3 pieces from a pizza cut into 4 pieces (3/4) or 3 pieces from a pizza cut into 8 pieces (3/8)? The bigger pieces (3/4) are better!'
  },
  {
    id: 4,
    title: 'Adding & Subtracting Fractions with Like Denominators',
    vocabulary: [
      { term: 'Like Denominators', definition: 'Fractions that have the same bottom number' },
      { term: 'Mixed Number', definition: 'A whole number and a fraction combined (like 2 ¾)' },
      { term: 'Improper Fraction', definition: 'A fraction where the numerator is greater than or equal to the denominator' },
      { term: 'Decomposing', definition: 'Breaking a fraction into smaller parts' }
    ],
    conceptExplanation: `When fractions have the same denominator, addition and subtraction are straightforward!

**Adding Fractions with Like Denominators:**
Add the numerators, keep the denominator the same.
Example: 2/8 + 3/8 = 5/8

**Subtracting Fractions with Like Denominators:**
Subtract the numerators, keep the denominator the same.
Example: 7/10 - 3/10 = 4/10 = 2/5 (simplified)

**Working with Mixed Numbers:**
Method 1: Convert to improper fractions, then add/subtract
Method 2: Add/subtract whole numbers and fractions separately
When subtracting, you may need to regroup (borrow from the whole number)

**Decomposing Fractions:**
Any fraction can be broken into unit fractions: 5/8 = 1/8 + 1/8 + 1/8 + 1/8 + 1/8`,
    examples: [
      {
        problem: '3/6 + 2/6',
        steps: [
          'The denominators are the same (both 6)',
          'Add the numerators: 3 + 2 = 5',
          'Keep the denominator: 6',
          'Answer: 5/6',
          'Check: Can we simplify? No, 5/6 is already in simplest form'
        ]
      },
      {
        problem: '2 ¾ + 1 ½ (mixed numbers)',
        steps: [
          'Convert ½ to fourths: ½ = 2/4',
          'Now we have: 2 ¾ + 1 2/4',
          'Add whole numbers: 2 + 1 = 3',
          'Add fractions: ¾ + 2/4 = 5/4',
          '5/4 is improper, so convert: 5/4 = 1 ¼',
          'Add to whole number: 3 + 1 ¼ = 4 ¼',
          'Answer: 4 ¼'
        ]
      },
      {
        problem: '5 1/5 - 2 4/5 (regrouping needed)',
        steps: [
          'We can\'t subtract 4/5 from 1/5, so we need to regroup',
          'Borrow 1 from 5: 5 1/5 = 4 + 1 + 1/5 = 4 6/5',
          'Now subtract whole numbers: 4 - 2 = 2',
          'Subtract fractions: 6/5 - 4/5 = 2/5',
          'Combine: 2 2/5',
          'Answer: 2 2/5'
        ]
      }
    ],
    practiceQuestions: [
      {
        question: 'What is 4/9 + 2/9?',
        options: ['6/9', '6/18', '2/3', 'Both A and C'],
        correctAnswer: 3,
        explanation: '4/9 + 2/9 = 6/9, which simplifies to 2/3. Both answers are correct!'
      },
      {
        question: 'Calculate 7/8 - 3/8',
        options: ['4/8', '1/2', '4/0', 'Both A and B'],
        correctAnswer: 3,
        explanation: '7/8 - 3/8 = 4/8, which simplifies to 1/2. Both are correct!'
      },
      {
        question: 'Add: 1 2/5 + 2 4/5',
        options: ['3 6/5', '4 1/5', '3 6/10', '4 3/5'],
        correctAnswer: 1,
        explanation: '1 2/5 + 2 4/5 = 3 6/5 = 3 + 1 1/5 = 4 1/5'
      },
      {
        question: 'Subtract: 6 1/6 - 2 5/6',
        options: ['3 2/6', '3 1/3', '4 4/6', '3 4/6'],
        correctAnswer: 1,
        explanation: 'Regroup: 6 1/6 = 5 7/6. Then: 5 7/6 - 2 5/6 = 3 2/6 = 3 1/3'
      }
    ],
    teacherTip: 'When adding or subtracting mixed numbers, I recommend converting to improper fractions first—it prevents regrouping mistakes! For example, 2 ¾ = 11/4. It might seem like more work, but it\'s actually more reliable, especially when you need to regroup.'
  },
  {
    id: 5,
    title: 'Decimal Notation: Tenths & Hundredths',
    vocabulary: [
      { term: 'Decimal', definition: 'A number that uses place value and a decimal point to show values less than one' },
      { term: 'Decimal Point', definition: 'The dot that separates the whole number from the fractional part' },
      { term: 'Tenths', definition: 'The first place to the right of the decimal point (1/10)' },
      { term: 'Hundredths', definition: 'The second place to the right of the decimal point (1/100)' }
    ],
    conceptExplanation: `Decimals are another way to represent fractions, especially fractions with denominators of 10, 100, 1000, etc.

**Place Value with Decimals:**
- Ones . Tenths Hundredths
- 3 . 4 5 means 3 ones, 4 tenths, 5 hundredths
- This equals 3 + 4/10 + 5/100 = 3 45/100

**Converting Fractions to Decimals:**
- Tenths: 3/10 = 0.3
- Hundredths: 47/100 = 0.47
- Mixed: 2 5/10 = 2.5

**Converting Decimals to Fractions:**
- 0.6 = 6/10 = 3/5 (simplified)
- 0.25 = 25/100 = 1/4 (simplified)

**Comparing Decimals:**
Line up the decimal points and compare digit by digit from left to right.`,
    examples: [
      {
        problem: 'Convert 7/10 to a decimal',
        steps: [
          'The denominator is 10, so this is in tenths',
          'Write 7 in the tenths place',
          'Answer: 0.7',
          'Check: 0.7 means 7 tenths, which is 7/10 ✓'
        ]
      },
      {
        problem: 'Convert 0.38 to a fraction',
        steps: [
          'Read the decimal: "thirty-eight hundredths"',
          'Write as a fraction: 38/100',
          'Simplify by dividing both by 2: 19/50',
          'Answer: 0.38 = 38/100 = 19/50'
        ]
      },
      {
        problem: 'Compare: 0.6 ___ 0.58',
        steps: [
          'Line up the decimal points: 0.60 and 0.58',
          'Add a zero to 0.6 to make comparison easier: 0.60',
          'Compare tenths: both have 6 tenths (tie)',
          'Compare hundredths: 0 hundredths vs 8 hundredths',
          '0 < 8, so 0.60 > 0.58',
          'Answer: 0.6 > 0.58'
        ]
      }
    ],
    practiceQuestions: [
      {
        question: 'What is 4/10 as a decimal?',
        options: ['0.04', '0.4', '4.0', '0.40'],
        correctAnswer: 1,
        explanation: '4/10 = 0.4 (4 in the tenths place). Note: 0.4 and 0.40 are equal, but 0.4 is the standard form.'
      },
      {
        question: 'Convert 0.75 to a fraction in simplest form',
        options: ['75/100', '7/10', '3/4', '15/20'],
        correctAnswer: 2,
        explanation: '0.75 = 75/100 = 3/4 (divide both by 25)'
      },
      {
        question: 'Which is greater: 0.8 or 0.79?',
        options: ['0.8', '0.79', 'They are equal', 'Cannot compare'],
        correctAnswer: 0,
        explanation: '0.8 = 0.80, and 0.80 > 0.79 (compare hundredths: 80 > 79)'
      },
      {
        question: 'What is 2 3/10 as a decimal?',
        options: ['2.03', '2.3', '2.30', 'Both B and C'],
        correctAnswer: 3,
        explanation: '2 3/10 = 2.3 (or 2.30, which is equivalent)'
      }
    ],
    teacherTip: 'Money is a great way to understand decimals! $0.25 is 25 cents, which is 25/100 or 1/4 of a dollar. $0.50 is 50 cents, which is 50/100 or 1/2 of a dollar. Whenever you work with money, you\'re working with decimals to the hundredths place!'
  },
  {
    id: 6,
    title: 'Angles & Geometry',
    vocabulary: [
      { term: 'Angle', definition: 'Two rays that share a common endpoint (vertex)' },
      { term: 'Vertex', definition: 'The common endpoint where two rays meet to form an angle' },
      { term: 'Protractor', definition: 'A tool used to measure angles in degrees' },
      { term: 'Acute Angle', definition: 'An angle measuring less than 90°' },
      { term: 'Right Angle', definition: 'An angle measuring exactly 90°' },
      { term: 'Obtuse Angle', definition: 'An angle measuring greater than 90° but less than 180°' },
      { term: 'Line of Symmetry', definition: 'A line that divides a shape into two identical halves' }
    ],
    conceptExplanation: `Understanding angles and geometric properties is essential in fourth grade!

**Measuring Angles:**
- Use a protractor to measure angles in degrees (°)
- Place the center point on the vertex
- Align one ray with 0°
- Read where the other ray points

**Classifying Angles:**
- Acute: less than 90° (sharp angle)
- Right: exactly 90° (square corner)
- Obtuse: between 90° and 180° (wide angle)
- Straight: exactly 180° (straight line)

**Classifying Triangles:**
By angles: acute, right, obtuse
By sides: equilateral (all equal), isosceles (2 equal), scalene (none equal)

**Lines of Symmetry:**
A line that divides a shape so both halves are mirror images.`,
    examples: [
      {
        problem: 'Classify an angle measuring 65°',
        steps: [
          'Compare to 90°: Is 65° less than, equal to, or greater than 90°?',
          '65° < 90°',
          'Angles less than 90° are acute',
          'Answer: 65° is an acute angle'
        ]
      },
      {
        problem: 'A triangle has angles of 90°, 45°, and 45°. Classify it by angles and sides.',
        steps: [
          'Check for right angle: Yes, one angle is 90°',
          'Classification by angles: Right triangle',
          'Check the sides: Two angles are equal (45° and 45°)',
          'When two angles are equal, two sides are equal',
          'Classification by sides: Isosceles triangle',
          'Answer: Right isosceles triangle'
        ]
      },
      {
        problem: 'How many lines of symmetry does a square have?',
        steps: [
          'Draw a square',
          'Try vertical line through center: Both halves match ✓',
          'Try horizontal line through center: Both halves match ✓',
          'Try diagonal from top-left to bottom-right: Both halves match ✓',
          'Try diagonal from top-right to bottom-left: Both halves match ✓',
          'Answer: A square has 4 lines of symmetry'
        ]
      }
    ],
    practiceQuestions: [
      {
        question: 'An angle measures 120°. What type of angle is it?',
        options: ['Acute', 'Right', 'Obtuse', 'Straight'],
        correctAnswer: 2,
        explanation: '120° is greater than 90° but less than 180°, so it is an obtuse angle'
      },
      {
        question: 'A triangle has all three sides of different lengths. What type of triangle is it?',
        options: ['Equilateral', 'Isosceles', 'Scalene', 'Right'],
        correctAnswer: 2,
        explanation: 'A triangle with all different side lengths is called a scalene triangle'
      },
      {
        question: 'How many lines of symmetry does a regular pentagon have?',
        options: ['3', '4', '5', '6'],
        correctAnswer: 2,
        explanation: 'A regular pentagon has 5 lines of symmetry—one through each vertex to the opposite side'
      },
      {
        question: 'Two angles in a triangle measure 60° and 80°. What is the measure of the third angle?',
        options: ['30°', '40°', '50°', '60°'],
        correctAnswer: 1,
        explanation: 'The sum of angles in a triangle is 180°. So: 180° - 60° - 80° = 40°'
      }
    ],
    teacherTip: 'When measuring angles with a protractor, make sure you\'re reading the correct scale! Most protractors have two sets of numbers. Start from 0° on the ray you\'ve aligned, and follow that scale around. If you start at the inner 0°, use the inner numbers. If you start at the outer 0°, use the outer numbers.'
  }
];